import {
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  NumberField,
  actionColumn,
  column,
  dragHandleColumn,
  emptyCell,
  insertAtIndex,
  numericColumn,
  simpleHeader,
} from "@homebound/beam";
import { ReactNode, useMemo, useRef, useState } from "react";
import { SaveReadyPlanLocationInput } from "src/generated/graphqlTypes";
import { PlanPackageCheckoutV2_LocationFragment } from "src/routes/libraries/plan-package-v2/components/CheckoutTab.generated";

type InsideMapsRoomsTableProps = {
  rooms: PlanPackageCheckoutV2_LocationFragment[];
  disabled: boolean | ReactNode;
  saveRpls: (inputs: SaveReadyPlanLocationInput[]) => Promise<void>;
};

export function InsideMapsRoomsTable(props: InsideMapsRoomsTableProps) {
  const { rooms, disabled, saveRpls } = props;
  const rows = useMemo<GridDataRow<Row>[]>(
    () => [
      simpleHeader,
      ...rooms.map((rpl) => ({ kind: "room" as const, id: rpl.id, data: rpl, draggable: !disabled })),
    ],
    [rooms, disabled],
  );

  function onRowDrop(dragged: GridDataRow<Row>, dropped: GridDataRow<Row>, indexOffset: number) {
    if (dragged.kind !== "room" || dropped.kind !== "room" || dragged.id === dropped.id) return;
    const withoutDragged = rooms.filter((rpl) => rpl.id !== dragged.id);
    const droppedIndex = withoutDragged.findIndex((rpl) => rpl.id === dropped.id) + indexOffset;
    const changes = renumberInputs(insertAtIndex(withoutDragged, dragged.data, droppedIndex));
    if (changes.nonEmpty) void saveRpls(changes);
  }

  function removeRoom(removed: PlanPackageCheckoutV2_LocationFragment) {
    void saveRpls([
      { id: removed.id, isInsideMapsRoom: false },
      ...renumberInputs(rooms.filter((rpl) => rpl.id !== removed.id)),
    ]);
  }

  const columns: GridColumn<Row>[] = [
    dragHandleColumn<Row>({ header: emptyCell }),
    numericColumn<Row>({
      header: { content: "Order", alignment: "left" },
      room: (rpl) => ({ content: () => <OrderCell rpl={rpl} rooms={rooms} disabled={disabled} saveRpls={saveRpls} /> }),
      w: "60px",
    }),
    column<Row>({ header: "Room", room: (rpl) => rpl.location.name }),
    actionColumn<Row>({
      header: emptyCell,
      room: (rpl) => <IconButton icon="trash" onClick={() => removeRoom(rpl)} disabled={disabled} />,
      w: "56px",
    }),
  ];

  return (
    <GridTable
      id="insideMapsRoomsTable"
      columns={columns}
      rows={rows}
      onRowDrop={onRowDrop}
      fallbackMessage="No rooms have been added to checkout."
      style={{ bordered: true, allWhite: true, inlineEditing: true, rowHeight: "fixed" }}
    />
  );
}

/** Renumbers `insideMapsRoomOrder` to a sequential 1..n order, returning save inputs for only the rows that change. */
export function renumberInputs(orderedRooms: PlanPackageCheckoutV2_LocationFragment[]): SaveReadyPlanLocationInput[] {
  return orderedRooms.flatMap((rpl, i) =>
    rpl.insideMapsRoomOrder === i + 1 ? [] : [{ id: rpl.id, insideMapsRoomOrder: i + 1 }],
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };
type RoomRow = { kind: "room"; id: string; data: PlanPackageCheckoutV2_LocationFragment };
type Row = HeaderRow | RoomRow;

type OrderCellProps = {
  rpl: PlanPackageCheckoutV2_LocationFragment;
  rooms: PlanPackageCheckoutV2_LocationFragment[];
  disabled: boolean | ReactNode;
  saveRpls: (inputs: SaveReadyPlanLocationInput[]) => Promise<void>;
};

/** An unbound field over the room's order so typed values only commit (and renumber siblings) on blur. */
function OrderCell(props: OrderCellProps) {
  const { rpl, rooms, disabled, saveRpls } = props;
  const [pending, setPending] = useState<number>();
  // Mirrors `pending` for commit() to read: blur can fire before the state update has rendered (e.g. paste, then tab)
  const pendingRef = useRef<number>();

  function commit() {
    const typed = pendingRef.current;
    pendingRef.current = undefined;
    setPending(undefined);
    if (typed === undefined || typed === rpl.insideMapsRoomOrder) return;
    // Move the edited room to its typed position (clamped to 1..n) and renumber the rest around it
    const reordered = rooms.filter((r) => r.id !== rpl.id);
    reordered.splice(Math.min(Math.max(1, typed), rooms.length) - 1, 0, rpl);
    const changes = renumberInputs(reordered);
    if (changes.nonEmpty) void saveRpls(changes);
  }

  return (
    <NumberField
      label="Order"
      labelStyle="hidden"
      value={pending ?? rpl.insideMapsRoomOrder ?? undefined}
      onChange={(value) => {
        pendingRef.current = value;
        setPending(value);
      }}
      onBlur={commit}
      positiveOnly
      disabled={disabled}
    />
  );
}
